import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { supabaseSelectWithRetry } from '@/lib/supabaseRetry';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface ConsortiumInvitation {
  id: string;
  consortium_id: string;
  inviter_id: string;
  invitee_id: string;
  invitation_message?: string;
  status: 'pending' | 'accepted' | 'declined';
  created_at: string;
  updated_at: string;
  consortium?: {
    consortium_name: string;
    opportunity_id: string;
    opportunities?: {
      title: string;
      company: string;
    };
  };
  inviter?: {
    name: string;
    company: string;
    image?: string;
  };
}

export const useConsortiumInvitations = () => {
  const [invitations, setInvitations] = useState<ConsortiumInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchInvitations = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // 🚀 REQUÊTE 1 : Invitations + Consortiums (JOIN qui fonctionne)
      const { data, error } = await supabaseSelectWithRetry(
        () => supabase
          .from('consortium_invitations')
          .select(`
            id,
            consortium_id,
            inviter_id,
            invitee_id,
            invitation_message,
            status,
            created_at,
            updated_at,
            opportunity_consortiums!consortium_id (
              consortium_name,
              opportunity_id,
              opportunities (
                title,
                company
              )
            )
          `)
          .eq('invitee_id', user.id)
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
      );

      if (error) throw error;

      if (data && data.length > 0) {
        // 🚀 REQUÊTE 2 : Batch fetch tous les inviters en une fois
        const inviterIds = [...new Set(data.map(inv => inv.inviter_id))].filter((id): id is string => !!id);
        
        const { data: invitersData } = await supabaseSelectWithRetry(
          () => supabase
            .from('club_members')
            .select('user_id, name, company, image')
            .in('user_id', inviterIds)
        );

        // Map pour O(1) lookup
        const invitersMap = new Map(
          (invitersData || []).map(inv => [inv.user_id, inv])
        );

        // Mapping avec lookup efficace
        const enrichedInvitations = data.map((invitation: any) => ({
          ...invitation,
          status: invitation.status as 'pending' | 'accepted' | 'declined',
          consortium: invitation.opportunity_consortiums ? {
            consortium_name: invitation.opportunity_consortiums.consortium_name,
            opportunity_id: invitation.opportunity_consortiums.opportunity_id,
            opportunities: invitation.opportunity_consortiums.opportunities
          } : undefined,
          inviter: invitersMap.get(invitation.inviter_id) || undefined
        }));

        setInvitations(enrichedInvitations);
      } else {
        setInvitations([]);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      
      // Log détaillé pour debug
      if (errorMessage.includes('PGRST200') || errorMessage.includes('relationship')) {
        console.error('❌ Erreur de relation FK détectée:', errorMessage);
      }
      
      // Ne pas spammer l'utilisateur avec des toasts pour timeouts ou erreurs temporaires
      if (!errorMessage.includes('timeout') && 
          !errorMessage.includes('deadline') &&
          !errorMessage.includes('aborted')) {
        toast.error('Erreur lors du chargement des invitations', {
          description: 'Veuillez réessayer dans quelques instants'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, [user]);

  const respondToInvitation = async (
    invitationId: string, 
    response: 'accepted' | 'declined'
  ): Promise<void> => {
    if (!user) {
      toast.error('Vous devez être connecté');
      return;
    }

    try {
      // 🚀 OPTIMISATION : Ajout retry sur l'update
      const { error: updateError } = await supabaseSelectWithRetry(
        () => supabase
          .from('consortium_invitations')
          .update({ 
            status: response,
            updated_at: new Date().toISOString()
          })
          .eq('id', invitationId)
      );

      if (updateError) throw updateError;

      if (response === 'accepted') {
        // Get invitation details to join consortium
        const invitation = invitations.find(inv => inv.id === invitationId);
        if (!invitation) {
          throw new Error('Invitation not found');
        }

        // Add user to consortium
        const { error: joinError } = await supabase
          .from('opportunity_consortiums')
          .insert({
            user_id: user.id,
            opportunity_id: invitation.consortium?.opportunity_id,
            consortium_name: invitation.consortium?.consortium_name,
            role_in_consortium: 'member',
            status: 'active'
          });

        if (joinError) throw joinError;

        toast.success('Invitation acceptée ! Vous avez rejoint le consortium.');
      } else {
        toast.success('Invitation déclinée.');
      }

      // Refresh invitations
      await fetchInvitations();
    } catch (error) {
      console.error('Error responding to invitation:', error);
      toast.error('Erreur lors de la réponse à l\'invitation');
    }
  };

  return {
    invitations,
    loading,
    respondToInvitation,
    refetch: fetchInvitations
  };
};